import { Table, TableHeader, TableBody } from '../../../components/table';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@iconify/react';
import { Dialog } from '../../../components/ui';
import StatusBadge from '../../../components/ui/StatusBadge';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { getAssessmentDetails, getAssessmentTraineeAnswers } from '../../../services/organization/courses/courseAnalytics.service';
import { decodeData } from '../../../utils/encodeAndEncode';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';
import { useCourseName } from './CourseNameHeading';

/** @param {unknown} raw */
function parseAssessmentField(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return raw;
    }
  }
  return raw;
}

/**
 * @param {unknown} options
 * @returns {{ key: string, label: string, text: string }[]}
 */
function optionLetterLabel(index) {
  if (index < 26) return String.fromCharCode(65 + index);
  return `${index + 1}`;
}

function normalizeQuestionOptions(options) {
  const parsed = parseAssessmentField(options);
  if (parsed === null || parsed === undefined) return [];
  if (Array.isArray(parsed)) {
    return parsed.map((option, i) => {
      const isObject = option !== null && typeof option === 'object';
      return {
        key: isObject ? String(option.id ?? option.option_key ?? i) : `i${i}`,
        label: isObject ? String(option.option_key || optionLetterLabel(i)) : optionLetterLabel(i),
        text: isObject ? String(option.text_fallback || option.content_value || option.text || option.option_value || 'Media option') : String(option ?? ''),
        correct: isObject && Number(option.is_correct) === 1,
      };
    });
  }
  if (typeof parsed === 'object') {
    const entries = Object.entries(parsed);
    return entries.map(([key, val], i) => {
      const isObject = val !== null && typeof val === 'object';
      return {
        key,
        label: optionLetterLabel(i),
        text: isObject ? String(val.text_fallback || val.content_value || val.text || val.option_value || 'Media option') : String(val ?? ''),
        correct: isObject && Number(val.is_correct) === 1,
      };
    });
  }
  return [{ key: '0', label: 'A', text: String(parsed) }];
}

/**
 * @param {unknown} correctOption
 * @returns {Set<string>}
 */
function buildCorrectAnswerSet(correctOption) {
  const parsed = parseAssessmentField(correctOption);
  const set = new Set();
  if (parsed === null || parsed === undefined) return set;
  const add = (v) => {
    if (v !== null && v !== undefined && v !== '') set.add(String(v).trim());
  };
  if (Array.isArray(parsed)) {
    parsed.forEach(add);
  } else {
    add(parsed);
  }
  return set;
}

function optionIsCorrect(opt, correctSet) {
  if (opt.correct) return true;
  if (correctSet.size === 0) return false;
  const text = opt.text.trim();
  if (correctSet.has(text)) return true;
  if (correctSet.has(opt.key)) return true;
  if (correctSet.has(opt.label)) return true;
  return false;
}

function formatAnswerValue(raw, options) {
  const parsed = parseAssessmentField(raw);
  if (parsed === null || parsed === undefined || (Array.isArray(parsed) && parsed.length === 0)) return 'Not answered';
  const choices = normalizeQuestionOptions(options);
  const values = Array.isArray(parsed) ? parsed : [parsed];
  return values.map((value) => {
    const token = value && typeof value === 'object'
      ? value.id ?? value.option_key ?? value.content_value ?? value.text ?? value.answer
      : value;
    const match = choices.find((choice) => [choice.key, choice.label, choice.text].includes(String(token)));
    return match?.text || String(token ?? '');
  }).filter(Boolean).join(', ') || 'Not answered';
}

function getCorrectAnswerText(question) {
  const choices = normalizeQuestionOptions(question.options);
  const marked = choices.filter((choice) => choice.correct).map((choice) => choice.text);
  if (marked.length) return marked.join(', ');
  const value = formatAnswerValue(question.correct_option, question.options);
  return value === 'Not answered' ? 'Not available' : value;
}

function AssessmentQuestionOptions({ options, correctOption }) {
  const opts = useMemo(() => normalizeQuestionOptions(options), [options]);
  const correctSet = useMemo(() => buildCorrectAnswerSet(correctOption), [correctOption]);

  if (opts.length === 0) {
    const hasCorrect = correctSet.size > 0 || (typeof correctOption === 'string' && correctOption.trim() !== '');
    if (!hasCorrect) return null;
    const display = correctSet.size > 0 ? Array.from(correctSet) : [String(correctOption)];
    return (
      <div className="tw-mt-4">
        <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mb-2">Answer</div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {display.map((ans) => (
            <StatusBadge key={ans} tone="success">
              <Icon icon="mdi:check-circle-outline" width={16} height={16} />
              {ans}
            </StatusBadge>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-mt-4">
      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mb-2">Options</div>
      <ul className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-flex tw-flex-col [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] ui-list-group ui-list-group-flush tw-border tw-rounded-xl tw-overflow-hidden"}>
        {opts.map((opt) => {
          const correct = optionIsCorrect(opt, correctSet);
          return (
            <li
              key={opt.key}
              className={`tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&:last-child]:tw-border-b-[0px] ui-list-group-item tw-flex tw-items-center tw-gap-2 tw-py-2 tw-px-4   ${
                correct ? "tw-bg-emerald-600 tw-bg-opacity-10 tw-border-emerald-600" : ""
              }`}
            >
              <StatusBadge tone={correct ? 'success' : 'neutral'} className="tw-min-w-[28px] tw-justify-center">
                {opt.label}
              </StatusBadge>
              <span className={`!tw-text-[12px] tw-grow tw-text-sm   ${correct ? "![font-weight:var(--font-weight-medium)] tw-font-medium" : ""}`}>{opt.text || '—'}</span>
              {correct ? (
                <Icon icon="mdi:check-circle" width={20} height={20} className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-shrink-0" aria-label="Correct answer" />
              ) : null}
            </li>
          );
        })}
      </ul>
      {correctSet.size > 0 &&
        !opts.some((opt) => optionIsCorrect(opt, correctSet)) && (
          <div className="!tw-text-[12px] tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 [--tw-border-opacity:1] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-warning tw-text-sm tw-mt-2 tw-mb-0 tw-py-2">
            Marked correct (no matching option): {Array.from(correctSet).join(', ')}
          </div>
        )}
    </div>
  );
}

function CourseAnalyticsAssessmentDetails() {
  const courseName = useCourseName();
  const { timeZone } = useTimezone();
  const formatAnalyticsDate = (value) => formatUtcInTimeZone(value, timeZone, 'MMM DD, YYYY');
  const { assessmentId, courseId } = useParams();
  const decodedCourseId = courseId ? decodeData(courseId) : null;
  const decodedAssessmentId = assessmentId ? decodeData(assessmentId) : null;
  const navigate = useNavigate();

  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [answerReview, setAnswerReview] = useState(null);
  const [answerReviewLoading, setAnswerReviewLoading] = useState(false);
  const [answerReviewError, setAnswerReviewError] = useState('');
  const answerRequestId = useRef(0);

  const fetchAssessmentDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAssessmentDetails(decodedAssessmentId, decodedCourseId);


      if (response.success && response.data) {
        setAssessmentDetails(response.data);
        setRecentAttempts(response.data.recent_attempts || []);
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
    } finally {
      setLoading(false);
    }

  }, [decodedAssessmentId, decodedCourseId]);

  useEffect(() => {
    fetchAssessmentDetails();
  }, [fetchAssessmentDetails]);

  const openAnswerReview = async (attempt) => {
    const requestId = ++answerRequestId.current;
    setSelectedAttempt(attempt);
    setAnswerReview(null);
    setAnswerReviewError('');
    setAnswerReviewLoading(true);
    try {
      const response = await getAssessmentTraineeAnswers(decodedAssessmentId, attempt.user_id);
      if (requestId !== answerRequestId.current) return;
      if (response?.success) setAnswerReview(response.data);
      else setAnswerReviewError(response?.message || 'Could not load saved answers.');
    } catch (error) {
      if (requestId === answerRequestId.current) {
        setAnswerReviewError(error?.response?.data?.message || 'Could not load saved answers.');
      }
    } finally {
      if (requestId === answerRequestId.current) setAnswerReviewLoading(false);
    }
  };

  const closeAnswerReview = () => {
    answerRequestId.current += 1;
    setSelectedAttempt(null);
    setAnswerReview(null);
    setAnswerReviewError('');
    setAnswerReviewLoading(false);
  };

  if (loading) {
    return <Loader variant="section" caption="Loading assessment analytics..." containerHeight="400px" />;
  }

  if (!assessmentDetails) {
    return (
      <div>
        <NoData message="Assessment not found" />
        <div className="tw-text-center tw-pb-6">
          <button className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"} onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const createdDateRaw = assessmentDetails.createdAt || assessmentDetails.created_at;
  const isActive = Number(assessmentDetails.is_active) === 1;
  const metricCards = [
    { label: 'Total Questions', value: assessmentDetails.total_questions ?? 0, icon: 'fluent:question-circle-24-regular', bgColor: '#F4F7FE', iconColor: 'tw-text-brand-600' },
    { label: 'Total Attempts', value: assessmentDetails.total_attempts ?? 0, icon: 'fluent:people-24-regular', bgColor: '#E6FAF5', iconColor: 'tw-text-emerald-600' },
    { label: 'Passing Percentage', value: `${assessmentDetails.pass_percentage ?? 0}%`, icon: 'fluent:checkmark-circle-24-regular', bgColor: '#FFF6E5', iconColor: 'tw-text-amber-600' },
  ];

  return (
    <>
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <h4 className="tw-mb-0 tw-break-words">{assessmentDetails.course_name || courseName || 'Course'}</h4>
        </div>
      </div>

      {/* Key assessment metrics */}
      <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
        {metricCards.map((card) => (
          <div key={card.label} className="tw-min-w-0 tw-rounded-xl tw-border tw-border-slate-200 tw-p-5 tw-shadow-sm" style={{ backgroundColor: card.bgColor }}>
            <div className="tw-mb-2 tw-flex tw-min-h-[40px] tw-items-start tw-gap-2">
              <Icon icon={card.icon} width={22} height={22} className={card.iconColor} />
              <span className="tw-text-sm tw-font-medium tw-text-slate-600">{card.label}</span>
            </div>
            <div className="tw-break-words tw-text-xl tw-font-semibold tw-text-slate-800">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Assessment information */}
      <section className="tw-mb-6 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-5 tw-shadow-sm">
        <h5 className="tw-mb-5 tw-text-lg tw-font-semibold tw-text-slate-800">About assessment</h5>
        <dl className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
          <div className="tw-min-w-0">
            <dt className="tw-mb-1 tw-text-sm tw-text-slate-500">Assessment Name</dt>
            <dd className="tw-mb-0 tw-break-words tw-font-medium tw-text-slate-800">{assessmentDetails.assessment_name || 'N/A'}</dd>
          </div>
          <div className="tw-min-w-0">
            <dt className="tw-mb-1 tw-text-sm tw-text-slate-500">Created Date</dt>
            <dd className="tw-mb-0 tw-font-medium tw-text-slate-800">{createdDateRaw ? formatAnalyticsDate(createdDateRaw) : 'N/A'}</dd>
          </div>
          <div className="tw-min-w-0">
            <dt className="tw-mb-1 tw-text-sm tw-text-slate-500">Points</dt>
            <dd className="tw-mb-0 tw-font-medium tw-text-slate-800">{assessmentDetails.earn_point ?? 0}</dd>
          </div>
          <div className="tw-min-w-0">
            <dt className="tw-mb-1 tw-text-sm tw-text-slate-500">Status</dt>
            <dd className="tw-mb-0">
              <StatusBadge tone={isActive ? 'success' : 'neutral'}>
                {isActive ? 'Active' : 'Inactive'}
              </StatusBadge>
            </dd>
          </div>
        </dl>
      </section>

      {/* Read-only question preview */}
      <section className="tw-mb-6 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-5 tw-shadow-sm">
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div>
            <h5 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-800">Assessment questions</h5>
            <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Read-only preview of the questions in this assessment.</p>
          </div>
          <StatusBadge tone="info">{assessmentDetails.questions?.length || 0} questions</StatusBadge>
        </div>
        {Array.isArray(assessmentDetails.questions) && assessmentDetails.questions.length > 0 ? (
          <>
            <div className="tw-flex tw-flex-col tw-gap-3">
              {assessmentDetails.questions.slice(0, showAllQuestions ? assessmentDetails.questions.length : 2).map((question, index) => {
                const questionKey = question.question_id ?? index;
                const isExpanded = expandedQuestionId === questionKey;
                const hasDetails = normalizeQuestionOptions(question.options).length > 0
                  || buildCorrectAnswerSet(question.correct_option).size > 0;
                const canExpand = hasDetails || String(question.question || '').length > 160;
                return (
                  <div key={questionKey} className="tw-min-w-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <span className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-700">{index + 1}</span>
                      <div className="tw-min-w-0 tw-flex-1">
                        <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
                          <p className={`tw-mb-0 tw-break-words tw-font-medium tw-text-slate-800 ${canExpand && !isExpanded ? 'tw-line-clamp-2' : ''}`} title={question.question || undefined}>{question.question || 'Question text unavailable'}</p>
                          <StatusBadge tone="neutral" className="tw-uppercase">{question.question_type || 'Question'}</StatusBadge>
                        </div>
                        {canExpand && (
                          <button
                            type="button"
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-indigo-600 hover:tw-text-indigo-800"
                            onClick={() => setExpandedQuestionId(isExpanded ? null : questionKey)}
                            aria-expanded={isExpanded}
                          >
                            <Icon icon={isExpanded ? 'fluent:chevron-up-20-regular' : 'fluent:chevron-down-20-regular'} width={16} height={16} />
                            {isExpanded ? 'Show less' : hasDetails ? 'View options and answer' : 'View full question'}
                          </button>
                        )}
                        {isExpanded && hasDetails && <AssessmentQuestionOptions options={question.options} correctOption={question.correct_option} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {assessmentDetails.questions.length > 2 && (
              <button
                type="button"
                className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-indigo-700 hover:tw-bg-indigo-100"
                onClick={() => {
                  setShowAllQuestions((value) => !value);
                  setExpandedQuestionId(null);
                }}
                aria-expanded={showAllQuestions}
              >
                <Icon icon={showAllQuestions ? 'fluent:chevron-up-20-regular' : 'fluent:chevron-down-20-regular'} width={16} height={16} />
                {showAllQuestions ? 'Show fewer questions' : 'Show all questions (' + assessmentDetails.questions.length + ')'}
              </button>
            )}
          </>
        ) : (
          <NoData message="No questions found for this assessment" />
        )}
      </section>

      {/* Latest recorded result per trainee */}
      <section className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <div className="tw-p-5">
          <h5 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-800">Recent Attempts</h5>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Latest recorded result for each trainee. Open View answers to review their responses.</p>
        </div>
        {recentAttempts.length === 0 ? (
          <NoData message="No attempts found for this assessment" />
        ) : (
          <div className="tw-w-full tw-overflow-x-auto">
            <Table className="tw-mb-0 tw-w-full tw-text-sm ui-table">
              <TableHeader>
                <tr className="tw-bg-slate-50 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Trainee</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Score</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Result</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Last activity</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Action</th>
                </tr>
              </TableHeader>
              <TableBody>
                {recentAttempts.map((attempt) => (
                  <tr key={attempt.user_id} className="tw-border-t tw-border-slate-100 hover:tw-bg-slate-50">
                    <td className="tw-min-w-[220px] tw-px-5 tw-py-4">
                      <div className="tw-font-medium tw-text-slate-800">{attempt.user_name || 'Unknown trainee'}</div>
                      <div className="tw-break-all tw-text-xs tw-text-slate-500">{attempt.email || 'No email'}</div>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4">
                      <div className="tw-font-semibold tw-text-slate-800">{attempt.score_percentage}%</div>
                      <div className="tw-text-xs tw-text-slate-500">{attempt.correct_answers}/{attempt.total_questions} correct</div>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4">
                      <StatusBadge tone={attempt.passed ? 'success' : 'danger'}>{attempt.passed ? 'Passed' : 'Failed'}</StatusBadge>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4 tw-text-slate-600">
                      {attempt.last_attempt_date ? formatAnalyticsDate(attempt.last_attempt_date) : 'N/A'}
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4">
                      <button
                        type="button"
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-white tw-px-3 tw-py-2 tw-font-medium tw-text-indigo-700 hover:tw-bg-indigo-50"
                        onClick={() => openAnswerReview(attempt)}
                      >
                        <Icon icon="fluent:eye-20-regular" width={18} height={18} />
                        View answers
                      </button>
                    </td>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <Dialog show={Boolean(selectedAttempt)} onHide={closeAnswerReview} size="lg" scrollable>
        <Dialog.Header closeButton>
          <Dialog.Title>Answers from {selectedAttempt?.user_name || 'trainee'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {answerReviewLoading ? (
            <div className="tw-py-10 tw-text-center tw-text-slate-500">Loading saved answers...</div>
          ) : answerReviewError ? (
            <div role="alert" className="tw-rounded-lg tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-4 tw-text-rose-700">{answerReviewError}</div>
          ) : answerReview ? (
            <>
              <p className="tw-mb-4 tw-text-sm tw-text-slate-600">
                {answerReview.answer_scope === 'latest_attempt'
                  ? 'Showing answers from the latest recorded attempt.'
                  : 'Showing the latest saved answer for each question. Older separate submissions are not stored as distinct attempts.'}
              </p>
              {answerReview.questions?.length ? (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {answerReview.questions.map((question, index) => {
                    const hasAnswer = question.user_answer !== null && question.user_answer !== undefined && question.user_answer !== '';
                    const answerStatus = !hasAnswer ? 'Not answered' : question.user_is_correct == null ? 'Not graded' : Number(question.user_is_correct) === 1 ? 'Correct' : 'Incorrect';
                    const answerTone = answerStatus === 'Correct' ? 'success' : answerStatus === 'Incorrect' ? 'danger' : 'neutral';
                    return (
                      <div key={question.question_id ?? index} className="tw-rounded-lg tw-border tw-border-slate-200 tw-p-4">
                        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
                          <div className="tw-min-w-0 tw-flex-1 tw-break-words tw-font-medium tw-text-slate-800">{index + 1}. {question.question || 'Question text unavailable'}</div>
                          <StatusBadge tone={answerTone}>{answerStatus}</StatusBadge>
                        </div>
                        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                          <div className={`tw-min-w-0 tw-rounded-lg tw-p-3 ${answerStatus === 'Correct' ? 'tw-bg-emerald-50' : answerStatus === 'Incorrect' ? 'tw-bg-rose-50' : 'tw-bg-slate-50'}`}>
                            <div className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">Trainee answer</div>
                            <div className="tw-break-words tw-text-sm tw-text-slate-800">{formatAnswerValue(question.user_answer, question.options)}</div>
                          </div>
                          <div className="tw-min-w-0 tw-rounded-lg tw-bg-emerald-50 tw-p-3">
                            <div className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-700">Correct answer</div>
                            <div className="tw-break-words tw-text-sm tw-text-slate-800">{getCorrectAnswerText(question)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <NoData message="No saved answers found for this trainee" />
              )}
            </>
          ) : null}
        </Dialog.Body>
        <Dialog.Footer>
          <button type="button" className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50" onClick={closeAnswerReview}>Close</button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}

export default CourseAnalyticsAssessmentDetails;
